// public/v3/src/lib/hooks/use-unsaved-changes-guard.js — shared React hook
// for v3 modals. Snapshots initial field values on first render, computes a
// dirty flag against current state on every render, and exposes an
// `attemptDismiss` handler that routes all dismissal paths (backdrop tap,
// Cancel button, X close, Esc key) through a single decision point.
//
// When dirty: caller renders an inline "Discard changes?" prompt instead of
// the form body. Keep editing → returns to the form with all values intact.
// Discard → invokes `onClose` immediately.
//
// Pure logic (`computeDirty`) lives in
// `public/v3/src/lib/use-unsaved-changes-guard-logic.js` — Jest-readable.
// This file is ESM (React import); it is NOT Jest-readable directly.
//
// See memory `v3-modal-unsaved-changes-guard` for the v3-wide rollout plan.

import { useRef, useMemo, useState, useCallback } from 'react';
import { computeDirty } from '../use-unsaved-changes-guard-logic';

export function useUnsavedChangesGuard({ values, onClose, busy = false }) {
  // Snapshot once on first render via a ref so the comparison baseline is
  // stable across re-renders. The caller is responsible for passing the same
  // shape every time (values is an object keyed by field name).
  const initial = useRef(values);
  const isDirty = useMemo(
    () => computeDirty(initial.current, values),
    [values]
  );
  const [showDiscardPrompt, setShowDiscardPrompt] = useState(false);

  const attemptDismiss = useCallback(() => {
    if (busy) return; // save in flight — ignore
    if (isDirty) { setShowDiscardPrompt(true); return; }
    onClose && onClose();
  }, [busy, isDirty, onClose]);

  const confirmDiscard = useCallback(() => {
    setShowDiscardPrompt(false);
    onClose && onClose();
  }, [onClose]);

  const keepEditing = useCallback(() => {
    setShowDiscardPrompt(false);
  }, []);

  return {
    isDirty,
    attemptDismiss,
    showDiscardPrompt,
    confirmDiscard,
    keepEditing,
  };
}
